import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  getOrdenTrabajo,
  useUpdateOrdenTrabajo,
} from "@/shared/actions/erp";
import { useRecordDirectLock, texto } from "@/shared";
import { useUiConfirmModalStore, useUiStore } from "@/store/ui";
import { useGeolocation } from "../../../../hooks";
import { OrdenTrabajo } from "@/shared/interfaces";

interface UseIniciarInstalacionParams {
  ordentrabajo: OrdenTrabajo;
  currentUser: { username?: string } | null;
  refreshData: () => Promise<void>;
}

//ERRORES IDENTIFICADOS
//ERR-RDS-101: Error al adquirir el lock por cambio de base de datos. Existe el registro para 
//    el usuario actual en redis pero el registro no se encuentra en la base. Se recomienda borrar el lock
//    manualmente en la base de redis del entorno.


export function useIniciarInstalacion({
  ordentrabajo,
  currentUser,
  refreshData,
}: UseIniciarInstalacionParams) {
  const navigate = useNavigate();
  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore((s) => s.setConfirmDialogIsOpen);
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);

  const { acquireLock, getUserLocks } = useRecordDirectLock(ordentrabajo.id_orden_trabajo);

  const udpdateInSiteUpdateOrdenTrabajo = useUpdateOrdenTrabajo<any>({
    enableRedirect: false,
  });

  const fechaHoraInicioInstalacion = ordentrabajo?.fecha_hora_inicio_instalacion;
  const hasFechaHoraInicioInstalacion =
    !!fechaHoraInicioInstalacion &&
    String(fechaHoraInicioInstalacion).trim() !== "" &&
    String(fechaHoraInicioInstalacion).toLowerCase() !== "null" &&
    String(fechaHoraInicioInstalacion).toLowerCase() !== "undefined";

  const { coords, permissionStatus, error, getCurrentCoords } =
    useGeolocation(!hasFechaHoraInicioInstalacion);

  const navegarAInstalacionEnCurso = useCallback(
    (dataLocks: any, ordenTrabajoLock: any) => {
      const uuid = dataLocks?.data?.[0]?.uuid;
      const estado = ordenTrabajoLock?.data?.estado_orden_trabajo;

      if (estado === "EN ESPERA") {
        navigate(`/tecnico/instalaciones/asignadas/${uuid}`);
      } else if (estado === "ESPERA_RECOORDINADA") {
        navigate(`/tecnico/instalaciones/asignadas/recoordinada/${uuid}`);
      } else {
        toast.error("Estado de orden de trabajo no válido");
      }
      setConfirmDialogIsOpen(false);
    },
    [navigate, setConfirmDialogIsOpen],
  );

  const confirmarInstalacionEnCurso = useCallback(
    (dataLocks: any, ordenTrabajoLock: any) => {
      const razonSocial = ordenTrabajoLock?.data?.prospecto_data?.razon_social;

      setConfirmDialog({
        isOpen: true,
        title: "Tiene una instalación iniciada",
        subtitle: `Tiene una instalación en curso sin finalizar${
          razonSocial ? ` (${razonSocial})` : ""
        }. ${texto.instalacion_en_curso_tecnico}`,
        onConfirm: () => navegarAInstalacionEnCurso(dataLocks, ordenTrabajoLock),
        onClose: () => setConfirmDialogIsOpen(false),
        confirmTextBtn: "SI",
        cancelTextBtn: "NO",
      });
    },
    [setConfirmDialog, setConfirmDialogIsOpen, navegarAInstalacionEnCurso],
  );

  const confirmarIniciarInstalacion = useCallback(
    (location: { lat: number; lng: number }) => {
      setConfirmDialog({
        isOpen: true,
        title: "Iniciar Instalación",
        subtitle: `Al iniciar la instalación se registrará la hora de inicio de instalación y se actualizarán las coordenadas: (${location.lat}, ${location.lng}). ¿Desea continuar?`,
        onConfirm: () => {
          const updateDataCoordenadas = ordentrabajo?.historial_cambios_coordenadas
            ? [...ordentrabajo.historial_cambios_coordenadas]
            : [];
          updateDataCoordenadas.push({
            usuario: currentUser?.username || "",
            fecha_cambio: new Date().toISOString(),
            coordenada_anterior: ordentrabajo?.coordenadas || "",
            coordenada_nueva: `${location.lat},${location.lng}`,
          });

          udpdateInSiteUpdateOrdenTrabajo.mutateAsync(
            {
              id: ordentrabajo?.id_orden_trabajo!,
              data: {
                fecha_hora_inicio_instalacion: dayjs(),
                coordenadas: `${location.lat},${location.lng}`,
                historial_cambios_coordenadas: updateDataCoordenadas,
              },
            },
            {
              onSuccess: async () => {
                refreshData();
                setConfirmDialogIsOpen(false);
                await acquireLock({ timeout: -1 });
              },
            },
          );
        },
        onClose: () => setConfirmDialogIsOpen(false),
        confirmTextBtn: "SI",
        cancelTextBtn: "NO",
      });
    },
    [
      ordentrabajo,
      currentUser,
      udpdateInSiteUpdateOrdenTrabajo,
      refreshData,
      setConfirmDialog,
      setConfirmDialogIsOpen,
      acquireLock,
    ],
  );

  const iniciarInstalacion = useCallback(async () => {
    const dataLocks: any = await getUserLocks();
    const ordenTrabajoLock = dataLocks?.data?.[0]?.uuid
      ? await getOrdenTrabajo(dataLocks.data[0].uuid)
      : null;

    if (dataLocks?.count > 0 && ordenTrabajoLock) {
      confirmarInstalacionEnCurso(dataLocks, ordenTrabajoLock);
      return;
    }

    if (dataLocks?.count === 0) {
      setIsGlobalLoading(true);
      const location = coords ?? (await getCurrentCoords());
      if (!location) {
        toast.error("No se pudo obtener la ubicación actual");
        setIsGlobalLoading(false);
        return;
      }
      if (coords) {
        setIsGlobalLoading(false);
      }
      confirmarIniciarInstalacion(location);
      return;
    }

    toast.error("Se presentó un error inesperado [ERR-RDS-101]. Intente de nuevo mas tarde o comuníquese con soporte.");
  }, [
    getUserLocks,
    confirmarInstalacionEnCurso,
    coords,
    getCurrentCoords,
    setIsGlobalLoading,
    confirmarIniciarInstalacion,
  ]);

  return {
    hasFechaHoraInicioInstalacion,
    fechaHoraInicioInstalacion,
    coords,
    permissionStatus,
    error,
    iniciarInstalacion,
  };
}